import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Car, 
  ArrowLeft,
  Calculator as CalcIcon,
  DollarSign,
  Percent,
  Clock,
  ChevronRight,
  AlertCircle,
  Info
} from "lucide-react";

export const Calculator = () => {
  const [values, setValues] = useState({
    vehiclePrice: 25000,
    downPayment: 2000,
    tradeInValue: 0,
    loanBalance: 0,
    apr: 7.99,
    termMonths: 60
  });

  const [errors, setErrors] = useState({});

  const updateValue = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setValues(prev => ({ ...prev, [field]: numValue }));
    
    // Clear error when updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateInputs = () => {
    const newErrors = {};
    
    if (values.vehiclePrice <= 0) {
      newErrors.vehiclePrice = "Vehicle price must be greater than 0";
    }
    if (values.downPayment < 0) {
      newErrors.downPayment = "Down payment cannot be negative";
    }
    if (values.tradeInValue < 0) {
      newErrors.tradeInValue = "Trade-in value cannot be negative";
    }
    if (values.loanBalance < 0) {
      newErrors.loanBalance = "Loan balance cannot be negative";
    }
    if (values.apr < 0) {
      newErrors.apr = "APR cannot be negative";
    }
    if (values.termMonths <= 0) {
      newErrors.termMonths = "Term must be at least 1 month";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculation = useMemo(() => {
    // Net trade-in (trade value minus what's owed)
    const netTradeIn = Math.max(0, values.tradeInValue - values.loanBalance);
    
    // Principal = Vehicle Price - Down Payment - Net Trade-In
    const principal = values.vehiclePrice - values.downPayment - netTradeIn;
    
    // Check for negative principal
    if (principal <= 0) {
      return {
        monthlyPayment: 0,
        totalPayment: 0,
        totalInterest: 0,
        principal: 0,
        netTradeIn,
        isValid: false,
        message: "Your down payment and trade-in cover the vehicle price!"
      };
    }
    
    const monthlyRate = values.apr / 100 / 12;
    const numPayments = values.termMonths;
    
    let monthlyPayment;
    let totalPayment;
    let totalInterest;
    
    if (values.apr === 0) {
      // Zero APR - simple division
      monthlyPayment = principal / numPayments;
      totalPayment = principal;
      totalInterest = 0;
    } else {
      // Standard amortization formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
      const factor = Math.pow(1 + monthlyRate, numPayments);
      monthlyPayment = principal * (monthlyRate * factor) / (factor - 1);
      totalPayment = monthlyPayment * numPayments;
      totalInterest = totalPayment - principal;
    }
    
    return {
      monthlyPayment,
      totalPayment,
      totalInterest,
      principal,
      netTradeIn,
      isValid: true,
      message: null
    };
  }, [values]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const termOptions = [24, 36, 48, 60, 72, 84];

  return (
    <div className="calculator-page">
      <Helmet>
        <title>Payment Calculator | Moto Money Finance</title>
        <meta name="description" content="Estimate your monthly car payment with our payment calculator. Enter vehicle price, down payment, trade-in value, and financing terms to see your estimated payment." />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/" className="nav-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </nav>
        </div>
      </header>

      <div className="calculator-container">
        <div className="calculator-card">
          <div className="calculator-header">
            <div className="calculator-icon">
              <CalcIcon size={32} />
            </div>
            <h1>Payment Estimator</h1>
            <p>
              Get an estimate of your monthly payment. Adjust the values below to see how different scenarios affect your payment.
            </p>
          </div>

          <div className="calculator-content">
            <div className="calculator-inputs">
              {/* Vehicle Price */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <DollarSign size={16} />
                  Vehicle Price (CAD)
                </label>
                <div className="calc-input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    className={`calc-input ${errors.vehiclePrice ? 'input-error' : ''}`}
                    value={values.vehiclePrice || ''}
                    onChange={(e) => updateValue('vehiclePrice', e.target.value)}
                    min="0"
                    step="500"
                    data-testid="calc-price-input"
                  />
                </div>
                {errors.vehiclePrice && (
                  <span className="field-error"><AlertCircle size={14} /> {errors.vehiclePrice}</span>
                )}
              </div>

              {/* Down Payment */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <DollarSign size={16} />
                  Down Payment (CAD)
                </label>
                <div className="calc-input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    className={`calc-input ${errors.downPayment ? 'input-error' : ''}`}
                    value={values.downPayment || ''}
                    onChange={(e) => updateValue('downPayment', e.target.value)}
                    min="0"
                    step="500"
                    data-testid="calc-down-input"
                  />
                </div>
                {errors.downPayment && (
                  <span className="field-error"><AlertCircle size={14} /> {errors.downPayment}</span>
                )}
              </div>

              {/* Trade-In Value */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <DollarSign size={16} />
                  Trade-In Value (Optional)
                </label>
                <div className="calc-input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    className={`calc-input ${errors.tradeInValue ? 'input-error' : ''}`}
                    value={values.tradeInValue || ''}
                    onChange={(e) => updateValue('tradeInValue', e.target.value)}
                    min="0"
                    step="500"
                    data-testid="calc-tradein-input"
                  />
                </div>
                {errors.tradeInValue && (
                  <span className="field-error"><AlertCircle size={14} /> {errors.tradeInValue}</span>
                )}
              </div>

              {/* Loan Balance on Trade-In */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <DollarSign size={16} />
                  Balance Owing on Trade-In (Optional)
                </label>
                <div className="calc-input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    className={`calc-input ${errors.loanBalance ? 'input-error' : ''}`}
                    value={values.loanBalance || ''}
                    onChange={(e) => updateValue('loanBalance', e.target.value)}
                    min="0"
                    step="500"
                    data-testid="calc-balance-input"
                  />
                </div>
                {errors.loanBalance && (
                  <span className="field-error"><AlertCircle size={14} /> {errors.loanBalance}</span>
                )}
              </div>

              {/* Estimated APR */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <Percent size={16} />
                  Estimated APR (%)
                </label>
                <div className="calc-input-wrapper">
                  <input
                    type="number"
                    className={`calc-input ${errors.apr ? 'input-error' : ''}`}
                    value={values.apr || ''}
                    onChange={(e) => updateValue('apr', e.target.value)}
                    min="0"
                    max="30"
                    step="0.25"
                    data-testid="calc-apr-input"
                  />
                  <span className="input-suffix">%</span>
                </div>
                {errors.apr && (
                  <span className="field-error"><AlertCircle size={14} /> {errors.apr}</span>
                )}
              </div>

              {/* Term */}
              <div className="calc-input-group">
                <label className="calc-label">
                  <Clock size={16} />
                  Loan Term (Months)
                </label>
                <div className="term-buttons">
                  {termOptions.map(term => (
                    <button
                      key={term}
                      type="button"
                      className={`term-btn ${values.termMonths === term ? 'active' : ''}`}
                      onClick={() => updateValue('termMonths', term)}
                      data-testid={`term-${term}-btn`}
                    >
                      {term} mo
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="calculator-results">
              <div className="result-card main-result">
                <span className="result-label">Estimated Monthly Payment</span>
                <span className="result-value" data-testid="monthly-payment">
                  {calculation.isValid ? formatCurrency(calculation.monthlyPayment) : '--'}
                </span>
                {!calculation.isValid && calculation.message && (
                  <span className="result-message">{calculation.message}</span>
                )}
              </div>

              <div className="result-breakdown">
                <div className="breakdown-item">
                  <span>Amount Financed</span>
                  <span data-testid="principal-amount">{formatCurrency(calculation.principal)}</span>
                </div>
                {calculation.netTradeIn > 0 && (
                  <div className="breakdown-item">
                    <span>Net Trade-In Credit</span>
                    <span className="positive">-{formatCurrency(calculation.netTradeIn)}</span>
                  </div>
                )}
                <div className="breakdown-item">
                  <span>Total Interest</span>
                  <span data-testid="total-interest">{formatCurrency(calculation.totalInterest)}</span>
                </div>
                <div className="breakdown-item total">
                  <span>Total of Payments</span>
                  <span data-testid="total-payment">{formatCurrency(calculation.totalPayment)}</span>
                </div>
              </div>

              <div className="disclaimer-box">
                <Info size={16} />
                <div>
                  <strong>Important:</strong> This is an estimate only. Actual rates and terms are subject to lender approval and may vary based on your credit profile. 
                  Taxes, fees, and other charges are not included in this calculation.
                </div>
              </div>

              <Link to="/apply" className="btn btn-primary apply-btn" data-testid="calc-apply-btn">
                Apply Now
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="site-footer" style={{ marginTop: '0' }}>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Calculator;
